// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * How use it:
 * 1. by dispatch:
 *      dispatch({ type: "xHeader/update", data: { key: "isStick", value: true } });
 *      data => it has "key" -> object key and value
 * 2. by handler:
 *      xHeaderUpdate({ key: "isStick", value: true });
 */

import { xHeaderHandlerType } from "@conklin/App/Handlers/XNG/Header";
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft, original } from "immer";

export type xHeaderStateType = Readonly<{
    isSticky?: boolean;
    registerMenu?: string[];
    unregisterMenu?: string;
    headerHeight?: string;
    stickyHeader?: string;
    notificationBar?: boolean;
    stickyMenu?: boolean;
    secondaryNavigationItems?: any[];
    mobileSearchOpen?: boolean;
}>;

export const initialHeaderState: xHeaderStateType = {
    isSticky: false,
    registerMenu: [],
    unregisterMenu: "",
    headerHeight: "",
    stickyHeader: "",
    notificationBar: true,
    stickyMenu: false,
    secondaryNavigationItems: [],
    mobileSearchOpen: false,
};

const reducer = {
    "xHeader/update": (draft: Draft<xHeaderStateType>, action: { data: xHeaderHandlerType }) => {
        if (action.data.key) {
            if (action.data.key === "registerMenu") {
                draft["registerMenu"]?.push(action.data.value);
            } else if (action.data.key === "unregisterMenu") {
                const currentStore = original(draft) as xHeaderStateType;
                draft["registerMenu"] = currentStore.registerMenu?.filter((item: string) => action.data.value !== item);
            } else {
                draft[action.data.key] = action.data.value;
            }
        }
    },
    "xHeader/isSticky": (draft: Draft<xHeaderStateType>, action: { isSticky: boolean }) => {
        draft.isSticky = action.isSticky;
    },
    "xHeader/notificationBar": (draft: Draft<xHeaderStateType>, action: { notificationBar: boolean }) => {
        draft.notificationBar = action.notificationBar;
    },
    "xHeader/stickyMenu": (draft: Draft<xHeaderStateType>, action: { stickyMenu: boolean }) => {
        draft.stickyMenu = action.stickyMenu;
    },
    "xHeader/setSecondaryNavigationItems": (
        draft: Draft<xHeaderStateType>,
        action: { secondaryNavigationItems: any[] },
    ) => {
        draft.secondaryNavigationItems = action.secondaryNavigationItems;
    },
};

const xHeader = createTypedReducerWithImmer(initialHeaderState, reducer);

(reducers as any).xHeader = xHeader;

export type xHeaderState = {
    xHeader: xHeaderStateType;
} & ApplicationState;

type xHeaderReducers = {
    [key: string]: typeof xHeader;
};

export type xHeaderActions = Parameters<xHeaderReducers[keyof xHeaderReducers]>[1] | AnyAction;
